import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, CloudSun, Cloud, CloudFog, CloudRain, Snowflake, CloudLightning, NavigationArrow, PaperPlaneTilt, PencilSimple, Trash, X, Wind, Drop, Package, Warning, ShieldCheck, Star, EyeSlash, Flag, Globe, Crown, ArrowRight, CurrencyDollar } from "@phosphor-icons/react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { STATUS, STATUS_DOT, ROLE_LABEL, fmtDate, fmtShootWhen, money, coverImage, onPropImgError, GRADE_OPTIONS, gradeColor } from "../lib/helpers";
import DeliveryCountdown from "./DeliveryCountdown";
import { CheckInButton } from "./CheckInButton";
import { weatherInfo } from "../lib/helpers";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "./ui/sheet";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

const WICONS = { sun: Sun, cloudsun: CloudSun, cloud: Cloud, fog: CloudFog, rain: CloudRain, snow: Snowflake, storm: CloudLightning };

function Stars({ value, onChange, size = 24 }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1" data-testid="review-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" data-testid={`review-star-${n}`} disabled={!onChange}
          onMouseEnter={() => onChange && setHover(n)} onMouseLeave={() => onChange && setHover(0)}
          onClick={() => onChange && onChange(n)}
          className={onChange ? "cursor-pointer" : "cursor-default"}>
          <Star size={size} weight={(hover || value) >= n ? "fill" : "regular"}
            className={(hover || value) >= n ? "text-[#B08D57]" : "text-[#CFC7B8]"} />
        </button>
      ))}
    </div>
  );
}

function AdminReviewCard({ p, onSaved }) {
  const moderate = async (body) => {
    try { const r = await client.post(`/properties/${p.id}/review/moderate`, body); onSaved(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update review"); }
  };
  const del = async () => {
    try { const r = await client.delete(`/properties/${p.id}/review`); onSaved(r.data); toast.success("Review removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove review"); }
  };
  return (
    <div className="border border-[#E9DDC6] rounded-md p-4 bg-white space-y-3" data-testid="admin-review-card">
      <div className="flex items-center gap-2">
        <Star size={16} weight="fill" className="text-[#B08D57]" />
        <span className="text-sm font-medium">Agent review</span>
        <div className="ml-auto flex items-center gap-1.5">
          {p.review_hidden && <span className="pill pill--info" data-testid="review-hidden-badge">Hidden</span>}
          {p.review_flagged && <span className="pill pill--overdue" data-testid="review-flagged-badge">Flagged</span>}
        </div>
      </div>
      <div className="flex items-center gap-1" data-testid="admin-review-stars">
        {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={15} weight={n <= p.review_rating ? "fill" : "regular"} className={n <= p.review_rating ? "text-[#B08D57]" : "text-[#CFC7B8]"} />)}
      </div>
      {p.review_text && <p className="text-sm text-[#1A1A1A]">“{p.review_text}”</p>}
      <p className="text-xs text-[#8A8782]">— {p.review_agent_name}{p.reviewed_at ? ` · ${fmtDate(p.reviewed_at)}` : ""}</p>
      <div className="flex items-center justify-between border-t border-[#F0ECE2] pt-3">
        <span className="text-sm text-[#5C5A58] flex items-center gap-1.5"><EyeSlash size={15} weight="duotone" />Hide from public page</span>
        <Switch data-testid="review-hide-switch" checked={!!p.review_hidden} onCheckedChange={(v) => moderate({ hidden: v })} />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm text-[#5C5A58] flex items-center gap-1.5"><Flag size={15} weight="duotone" />Flag for follow-up</span>
        <Switch data-testid="review-flag-switch" checked={!!p.review_flagged} onCheckedChange={(v) => moderate({ flagged: v })} />
      </div>
      <Button data-testid="review-delete" variant="outline" onClick={del} className="w-full border-red-200 text-red-700 hover:bg-red-50"><Trash size={15} className="mr-1.5" />Delete review</Button>
    </div>
  );
}

function ReviewCard({ p, photographerName, onSaved }) {
  const [rating, setRating] = useState(p.review_rating || 0);
  const [text, setText] = useState(p.review_text || "");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!rating) return toast.error("Please pick a star rating");
    setSaving(true);
    try {
      const r = await client.post(`/properties/${p.id}/review`, { rating, text });
      onSaved(r.data);
      toast.success(`Thanks — your review is now on ${photographerName}'s page`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit review"); }
    finally { setSaving(false); }
  };
  return (
    <div className="border border-[#B08D57]/40 bg-[#B08D57]/10 rounded-md p-4 space-y-3" data-testid="review-card">
      <div className="flex items-center gap-2">
        <Star size={16} weight="fill" className="text-[#B08D57]" />
        <span className="text-sm font-medium text-[#4A0000]">{p.review_rating ? "Your review" : `Rate ${photographerName}`}</span>
      </div>
      <p className="text-xs text-[#5C5A58]">Your rating and note appear on {photographerName}'s public booking page.</p>
      {p.review_hidden && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="review-hidden-note">This review is currently hidden by the Reelty team.</p>}
      <Stars value={rating} onChange={setRating} />
      <textarea data-testid="review-text" value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={300}
        placeholder={`Share how the shoot went with ${photographerName}…`}
        className="w-full px-3 py-2 rounded-md border border-[#E9DDC6] text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#B08D57]" />
      <Button data-testid="review-submit" onClick={submit} disabled={saving} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white rounded-[5px] px-6">
        {saving ? "Saving…" : p.review_rating ? "Update review" : "Submit review"}
      </Button>
    </div>
  );
}

const WGRAD = {
  sun: "from-[#F6A93B] via-[#EC7A34] to-[#C6521C]",
  cloudsun: "from-[#7BA6C9] via-[#5C86AE] to-[#3E6588]",
  cloud: "from-[#7E8B99] via-[#5E6B7A] to-[#434E5C]",
  fog: "from-[#9AA0A6] via-[#767C83] to-[#565B61]",
  rain: "from-[#4E7CA6] via-[#365E86] to-[#22415F]",
  snow: "from-[#8FB4CE] via-[#6E97B6] to-[#4F7492]",
  storm: "from-[#5B5470] via-[#3C3550] to-[#241F35]",
};

function Weather({ propertyId, shootDate }) {
  const [w, setW] = useState(null);
  useEffect(() => {
    setW(null);
    client.get(`/properties/${propertyId}/weather`).then((r) => setW(r.data)).catch(() => setW({ available: false }));
  }, [propertyId, shootDate]);
  if (!w) return <div className="border border-[#E9DDC6] rounded-md p-4 text-sm text-[#5C5A58]">Loading weather…</div>;
  if (!w.available) return <div className="border border-[#E9DDC6] rounded-md p-4 text-sm text-[#5C5A58]">Weather unavailable for this location.</div>;
  const info = weatherInfo(w.code);
  const Icon = WICONS[info.icon] || Cloud;
  const grad = WGRAD[info.icon] || WGRAD.cloud;
  const fxClass = (info.icon === "rain" || info.icon === "storm") ? "wx-rain"
    : info.icon === "snow" ? "wx-snow"
    : (info.icon === "sun" || info.icon === "cloudsun") ? "wx-sun" : "";
  const maxPrecip = (w.hourly || []).reduce((m, h) => Math.max(m, h.precip || 0), 0);
  const isSevere = info.icon === "storm" || (w.wind || 0) >= 30;
  const rainLikely = !isSevere && (info.icon === "rain" || maxPrecip >= 50);
  const fmtHr = (h) => { const x = h % 12 === 0 ? 12 : h % 12; return `${x}${h < 12 ? "a" : "p"}`; };
  return (
    <div className={`relative rounded-md overflow-hidden text-white bg-gradient-to-br ${grad} shadow-md ${fxClass}`} data-testid="weather-widget">
      <div className="relative z-10 p-4 flex items-center gap-4">
        <Icon size={52} weight="fill" className="drop-shadow" color="#ffffff" />
        <div>
          <p className="font-mono-num text-4xl leading-none drop-shadow-sm">{w.temp != null ? `${Math.round(w.temp)}°` : "—"}</p>
          <p className="text-sm font-medium opacity-95">{info.label}</p>
        </div>
        <div className="ml-auto text-xs space-y-1 text-right opacity-95">
          <p className="flex items-center gap-1 justify-end"><Wind size={14} weight="fill" /> {w.wind != null ? `${Math.round(w.wind)} mph` : "—"}</p>
          <p className="flex items-center gap-1 justify-end"><Drop size={14} weight="fill" /> {w.humidity != null ? `${w.humidity}%` : "—"}</p>
        </div>
      </div>
      {w.stale && (
        <div className="relative z-10 px-4 py-1 text-[10px] bg-black/25 opacity-90" data-testid="weather-stale">
          Showing last available forecast — live weather is temporarily unavailable.
        </div>
      )}
      {(isSevere || rainLikely) && (
        <div data-testid="weather-badge"
          className={`relative z-10 px-4 py-1.5 text-xs font-semibold flex items-center gap-1.5 ${isSevere ? "bg-red-600/90" : "bg-black/30"}`}>
          {isSevere
            ? <><CloudLightning size={14} weight="fill" /> Severe weather likely around shoot time</>
            : <><CloudRain size={14} weight="fill" /> Rain likely{maxPrecip ? ` · up to ${maxPrecip}% chance` : ""}</>}
        </div>
      )}
      {w.hourly && w.hourly.length > 0 && (
        <div className="relative z-10 bg-black/20 backdrop-blur-sm px-3 py-3" data-testid="weather-range">
          <p className="text-[10px] uppercase tracking-widest opacity-80 mb-2 px-1">
            {w.has_shoot_hour ? "Around shoot time" : "Next hours"}
          </p>
          <div className="flex justify-between gap-1">
            {w.hourly.map((hr) => {
              const hi = weatherInfo(hr.code);
              const HIcon = WICONS[hi.icon] || Cloud;
              return (
                <div key={hr.time} data-testid={`weather-hour-${hr.hour}`}
                  className={`flex-1 flex flex-col items-center gap-1 rounded-md py-2 px-1 transition ${hr.is_shoot ? "bg-white/25 ring-1 ring-white/60" : ""}`}>
                  <span className={`text-[10px] ${hr.is_shoot ? "font-bold" : "opacity-80"}`}>{hr.is_shoot ? "Shoot" : fmtHr(hr.hour)}</span>
                  <HIcon size={20} weight="fill" color="#ffffff" />
                  <span className="font-mono-num text-sm">{hr.temp != null ? `${Math.round(hr.temp)}°` : "—"}</span>
                  <span className="flex items-center gap-0.5 text-[9px] opacity-80"><Drop size={9} weight="fill" />{hr.precip != null ? `${hr.precip}%` : "—"}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Messaging({ propertyId }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const boxRef = useRef(null);
  const bottomRef = useRef(null);

  const fetchMsgs = async () => {
    const r = await client.get(`/properties/${propertyId}/messages`);
    setMsgs(r.data);
  };
  useEffect(() => {
    fetchMsgs();
    const iv = setInterval(fetchMsgs, 3000);
    return () => clearInterval(iv);
  }, [propertyId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const t = text;
    setText("");
    await client.post(`/properties/${propertyId}/messages`, { text: t });
    fetchMsgs();
  };

  const bubble = (role, mine) => {
    if (role === "system") return "bg-red-50 text-red-800 border border-red-200";
    if (role === "guest") return "bg-[#B08D57]/12 text-[#3A2A12] border border-[#B08D57]/40 whitespace-pre-line";
    if (mine) return "bg-[#4A0000] text-white";
    if (role === "admin") return "bg-[#B08D57]/25 text-[#1A1A1A] border border-[#B08D57]/40";
    if (role === "photographer") return "bg-[#FAF9F6] text-[#1A1A1A] border border-[#E9DDC6]";
    return "bg-white text-[#1A1A1A] border border-[#E9DDC6]";
  };

  return (
    <div className="flex flex-col border border-[#E9DDC6] rounded-md overflow-hidden" data-testid="messaging-panel">
      <div ref={boxRef} className="reelty-scroll h-72 overflow-y-auto p-4 space-y-3 bg-[#FBFAF7]">
        {msgs.length === 0 && <p className="text-sm text-[#5C5A58] text-center py-8">No messages yet. Start the conversation.</p>}
        <AnimatePresence initial={false}>
          {msgs.map((m) => {
            const mine = m.sender_id === user.id;
            return (
              <motion.div key={m.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
                <span className="text-xs text-[#5C5A58] mb-1 px-1">{m.sender_name} · {ROLE_LABEL[m.sender_role]}</span>
                <div className={`max-w-[80%] px-3 py-2 rounded-md text-sm ${bubble(m.sender_role, mine)}`}>{m.text}</div>
              </motion.div>
            );
          })}
        </AnimatePresence>
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 p-3 border-t border-[#E9DDC6] bg-white">
        <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Message about this property…"
          className="flex-1 px-3 py-2 rounded-md border border-[#E9DDC6] text-sm focus:outline-none focus:ring-2 focus:ring-[#B08D57]" />
        <Button data-testid="message-send-button" type="submit" className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white px-3">
          <PaperPlaneTilt size={18} weight="fill" />
        </Button>
      </form>
    </div>
  );
}

export default function PropertyDetail({ propertyId, open, onClose, users, onChanged, onEdit, onManageLeadLander, onGoPenthouse, focus }) {
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [paying, setPaying] = useState(false);
  const payInvoice = async () => {
    if (!p) return;
    setPaying(true);
    try {
      const r = await client.post("/payments/checkout", { property_id: p.id, origin_url: window.location.origin, tip: 0 });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout. Please try again.");
      setPaying(false);
    }
  };
  const msgSectionRef = useRef(null);
  const deliverablesRef = useRef(null);
  const reviewRef = useRef(null);
  const isAdmin = user.role === "admin";
  const isEmployee = user.role === "employee";
  const isStaff = isAdmin || isEmployee;
  const isPhotog = user.role === "photographer";
  const isPilot = user.role === "pilot";
  const isCrew = isPhotog || isPilot || user.role === "dual";

  useEffect(() => {
    if (!propertyId) return;
    client.get(`/properties/${propertyId}`).then((r) => setP(r.data)).catch((e) => {
      if (e.response?.status === 404) toast.error("This shoot is no longer available.");
      else toast.error("Couldn't open that shoot.");
      onClose && onClose();
    });
  }, [propertyId]);

  useEffect(() => {
    if (!open || !p) return;
    const map = { messages: msgSectionRef, deliverables: deliverablesRef, review: reviewRef };
    const ref = map[focus];
    if (!ref) return;
    const t = setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: focus === "messages" ? "start" : "center" }), 400);
    return () => clearTimeout(t);
  }, [open, p, focus]);

  if (!p) return <Sheet open={open} onOpenChange={(v) => !v && onClose()}><SheetContent><SheetTitle className="sr-only">Shoot details</SheetTitle><SheetDescription className="sr-only">Loading shoot details</SheetDescription></SheetContent></Sheet>;

  const userName = (id) => { const u = users.find((x) => x.id === id); return u ? (u.name || u.email) : "Unassigned"; };
  const patch = async (body) => {
    const r = await client.patch(`/properties/${p.id}`, body);
    setP(r.data);
    onChanged();
  };
  const remove = async () => {
    await client.delete(`/properties/${p.id}`);
    toast.success("Shoot deleted");
    onChanged();
    onClose();
  };
  const release = async () => {
    try {
      const r = await client.post(`/properties/${p.id}/release`);
      toast.success(r.data.promoted ? "Released — your backup is now the photographer" : "Released — shoot reopened for claiming");
      onChanged();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not release shoot"); }
  };
  const addBackup = async () => {
    try { const r = await client.post(`/properties/${p.id}/backup`); setP(r.data); onChanged(); toast.success("You're on standby as a backup"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add as backup"); }
  };
  const withdrawBackup = async () => {
    try { const r = await client.delete(`/properties/${p.id}/backup`); setP(r.data); onChanged(); toast.success("Withdrawn as backup"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not withdraw"); }
  };
  const adminRelease = async () => {
    try {
      const r = await client.post(`/properties/${p.id}/admin-release`);
      setP(r.data.property);
      onChanged();
      toast.success(r.data.promoted
        ? `Reassigned to backup #1 — ${userName(r.data.property.photographer_id)} is now the photographer`
        : "Photographer released — shoot moved to Open Shoots");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not release photographer"); }
  };
  const backupList = p.backups || [];
  const iAmPrimary = isPhotog && p.photographer_id === user.id;
  const iAmBackup = isPhotog && backupList.some((b) => b.id === user.id);
  const canBackup = isPhotog && p.photographer_id && !iAmPrimary && !iAmBackup && backupList.length < 2;
  const hoursToShoot = p.shoot_date ? (new Date(p.shoot_date) - new Date()) / 36e5 : null;
  const lastMinute = hoursToShoot != null && hoursToShoot >= 0 && hoursToShoot < 24;
  const st = STATUS[p.status] || STATUS.scheduled;
  const mapUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(p.address)}`;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto reelty-scroll p-0 bg-[#FBFAF7]" data-testid="property-detail">
        <SheetTitle className="sr-only">{p.title}</SheetTitle>
        <SheetDescription className="sr-only">Details, weather, status, pay and messages for {p.title}</SheetDescription>
        <div className="h-44 bg-[#FAF9F6] relative">
          {<img src={coverImage(p)} alt={p.title} onError={onPropImgError} className="w-full h-full object-cover" />}
          <button onClick={onClose} className="absolute top-4 right-4 bg-white/90 rounded-full p-1.5 hover:bg-white" data-testid="detail-close"><X size={18} /></button>
          <span data-testid="detail-status-badge"
            className="absolute bottom-4 left-6 inline-flex items-center gap-1.5 rounded-[5px] bg-white/90 backdrop-blur-md border border-white/70 px-2.5 py-1 text-xs font-semibold text-[#2C2626] shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_DOT[p.status] || "#4A0000" }} />
            {st.label}
          </span>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <h2 className="font-display text-4xl tracking-tight">{p.title}</h2>
            <p className="text-[#5C5A58] mt-1">{p.address}</p>
            <p className="text-sm text-[#5C5A58] mt-1">Shoot: {fmtShootWhen(p.shoot_date)}</p>
            {p.package_type && (
              <div className="flex items-center gap-2 mt-3" data-testid="detail-package-badge">
                <Badge className="bg-[#4A0000]/5 text-[#4A0000] border-[#4A0000]/20 flex items-center gap-1"><Package size={12} weight="fill" />{p.package_type}{(isAdmin && p.package_price) ? ` · $${p.package_price}` : ""}</Badge>
                {p.drone && <Badge data-testid="detail-drone-badge" className="bg-[#B08D57]/15 text-[#8A6D1B] border-[#B08D57]/40">+ Drone</Badge>}
              </div>
            )}
          </div>

          {(isStaff || user.role === "agent") && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => onEdit(p)} data-testid="detail-edit" className="border-[#E9DDC6]"><PencilSimple size={16} className="mr-1" />{isStaff ? "Edit" : "Reschedule"}</Button>
              {isAdmin && <Button variant="outline" size="sm" onClick={remove} data-testid="detail-delete" className="border-red-200 text-red-700 hover:bg-red-50"><Trash size={16} className="mr-1" />Delete</Button>}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="border border-[#E9DDC6] rounded-md p-3 bg-white">
              <p className="text-xs text-[#5C5A58]">Agent</p>
              <p className="font-medium">{userName(p.agent_id)}</p>
            </div>
            <div className="border border-[#E9DDC6] rounded-md p-3 bg-white">
              <p className="text-xs text-[#5C5A58]">Photographer</p>
              {isAdmin ? (
                <Select value={p.photographer_id || "none"} onValueChange={(v) => patch({ photographer_id: v === "none" ? null : v })}>
                  <SelectTrigger className="mt-1 h-8 bg-white border-[#E9DDC6]" data-testid="detail-assign-photographer"><SelectValue placeholder="Assign photographer" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {users.filter((u) => u.role === "photographer" && u.active !== false).map((u) => (
                      <SelectItem key={u.id} value={u.id} data-testid={`assign-option-${u.id}`}>{u.name || u.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : <p className="font-medium">{userName(p.photographer_id)}</p>}
            </div>
          </div>

          {/* Backups / standby coverage */}
          {(p.photographer_id || backupList.length > 0) && (
            <div className="border border-[#E9DDC6] rounded-md p-3 bg-white space-y-3" data-testid="backups-roster">
              <div className="flex items-center gap-2">
                <ShieldCheck size={16} weight="fill" color="#4A0000" />
                <p className="text-sm font-medium">Backups <span className="text-xs text-[#5C5A58]">({backupList.length}/2)</span></p>
              </div>
              {backupList.length ? (
                <ol className="space-y-1">
                  {backupList.map((b, i) => (
                    <li key={b.id} className="text-sm flex items-center gap-2">
                      <Badge className="bg-[#B08D57]/15 text-[#8A6D1B] border-[#B08D57]/40">#{i + 1}</Badge>
                      <span>{b.name}</span>
                      {b.id === user.id && <span className="text-xs text-[#5C5A58]">(you)</span>}
                    </li>
                  ))}
                </ol>
              ) : <p className="text-xs text-[#5C5A58]">No backups yet.</p>}
              {canBackup && <Button data-testid="detail-add-backup" onClick={addBackup} className="w-full bg-[#4A0000] hover:bg-[#5C1A1A] text-white">Add me as backup</Button>}
              {iAmBackup && <Button data-testid="detail-withdraw-backup" variant="outline" onClick={withdrawBackup} className="w-full border-[#E9DDC6]">Withdraw as backup</Button>}
              {isAdmin && p.photographer_id && (
                <div className="pt-2 border-t border-[#F0ECE2] space-y-1.5">
                  <p className="text-xs text-[#5C5A58]">
                    {backupList.length
                      ? `Releasing ${userName(p.photographer_id)} hands it to backup #1 (${backupList[0].name}).`
                      : `No backups — releasing ${userName(p.photographer_id)} returns it to Open Shoots for claiming.`}
                  </p>
                  <Button data-testid="admin-release-photographer" onClick={adminRelease}
                    className="w-full bg-white border border-[#4A0000] text-[#4A0000] hover:bg-[#4A0000] hover:text-white transition-colors">
                    Release photographer{backupList.length ? " → promote backup #1" : " → Open Shoots"}
                  </Button>
                </div>
              )}
            </div>
          )}

          {iAmPrimary && (
            <div className="border border-[#4A0000]/20 rounded-md p-3 bg-white space-y-2" data-testid="release-section">
              <p className="text-sm font-medium">Can't make it?</p>
              <p className="text-xs text-[#5C5A58]">
                {backupList.length ? `Releasing hands it to backup #1 (${backupList[0].name}).` : "No backups — releasing returns it to the Open Shoots portal for another photographer to claim."}
                {lastMinute ? " ⚠ Less than 24h to the shoot — releasing now is last-minute." : " More than 24h out — no repercussions."}
              </p>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button data-testid="detail-release-shoot"
                    className="w-full bg-white border border-[#4A0000] text-[#4A0000] hover:bg-[#4A0000] hover:text-white transition-colors">
                    Can't make it? Release to team
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="release-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Release this shoot?</AlertDialogTitle>
                    <AlertDialogDescription>
                      {backupList.length
                        ? `This shoot will be handed to your backup, ${backupList[0].name}, and it will no longer be on your schedule.`
                        : "This shoot will be removed from your schedule and returned to the Open Shoots portal for another photographer to claim."}
                      {p.commission_photographer_id === user.id ? " You'll still keep your referral commission." : ""}
                      {lastMinute ? " Heads up: it's less than 24 hours away, so this is a last-minute release." : ""}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="release-cancel">Keep it</AlertDialogCancel>
                    <AlertDialogAction data-testid="release-confirm" onClick={release}
                      className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">Yes, release to team</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}

          {/* Status control — only admins can change a shoot's status */}
          <div>
            <p className="text-xs uppercase tracking-wide text-[#5C5A58] mb-2">Status</p>
            {isAdmin ? (
              <Select value={p.status} onValueChange={(v) => patch({ status: v })}>
                <SelectTrigger className="bg-white border-[#E9DDC6]" data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Badge data-testid="status-readonly" className={`border ${st.cls}`}>{st.label}</Badge>
            )}
          </div>

          {isCrew && (p.photographer_id === user.id || p.pilot_id === user.id) && (
            <CheckInButton p={p} user={user} onChanged={(np) => setP(np)} />
          )}

          <Weather propertyId={p.id} shootDate={p.shoot_date} />

          {p.weather_alert && (
            <div data-testid="weather-alert-banner"
              className={`rounded-md p-3 flex items-start gap-2 border ${p.weather_alert.level === "severe" ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
              <Warning size={20} weight="fill" className="shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold">{p.weather_alert.level === "severe" ? "Severe weather alert" : "Weather watch"}</p>
                <p className="text-xs">{p.weather_alert.label} forecast around {fmtDate(p.weather_alert.time)} · {(p.weather_alert.time || "").slice(11, 16)} at the shoot location{p.weather_alert.wind_gust ? ` · gusts ${p.weather_alert.wind_gust} mph` : ""}.</p>
              </div>
            </div>
          )}

          <a href={mapUrl} target="_blank" rel="noreferrer" data-testid="directions-link"
            className="flex items-center justify-center gap-2 w-full border border-[#4A0000] text-[#4A0000] rounded-md py-2.5 text-sm font-medium hover:bg-[#4A0000] hover:text-white transition-colors">
            <NavigationArrow size={18} weight="duotone" /> Get Directions
          </a>

          {/* LeadLander — public property page. Managed on its own dedicated section. */}
          {(isAdmin || user.role === "agent") && (() => {
            const isOwnerAgent = user.role === "agent" && p.agent_id === user.id;
            const canManage = isAdmin || (isOwnerAgent && user.penthouse_partner);
            return (
              <div className="border border-[#B08D57]/40 rounded-md bg-[#B08D57]/[0.06] p-4 space-y-3" data-testid="leadlander-card">
                <div className="flex items-center gap-2">
                  <Globe size={16} weight="duotone" color="#4A0000" />
                  <span className="text-sm font-medium text-[#4A0000]">LeadLander page</span>
                  <Badge className="ml-auto bg-[#B08D57]/15 text-[#8A6D1B] border-[#B08D57]/40 flex items-center gap-1"><Crown size={11} weight="fill" />Partner perk</Badge>
                </div>
                <p className="text-xs text-[#5C5A58]">This property has a public marketing page. {canManage ? "Manage its content, open houses and incoming leads." : "Upgrade to Penthouse Partner to receive and manage leads."}</p>
                {canManage ? (
                  <Button data-testid="ll-manage-btn" onClick={() => onManageLeadLander && onManageLeadLander(p.id)}
                    className="w-full bg-[#4A0000] hover:bg-[#5C1A1A] text-white rounded-[5px]">
                    Manage LeadLander page <ArrowRight size={15} weight="bold" className="ml-1.5" />
                  </Button>
                ) : (
                  <Button data-testid="ll-upsell-btn" onClick={() => onGoPenthouse && onGoPenthouse()}
                    className="w-full bg-gradient-to-r from-[#C6A253] to-[#B08D57] text-[#1A1715] hover:from-[#D0AC5C] hover:to-[#B99A5B] rounded-[5px] font-semibold">
                    <Crown size={15} weight="fill" className="mr-1.5" /> Unlock with Penthouse Partner
                  </Button>
                )}
              </div>
            );
          })()}

          {/* Deliverables — agent-facing link (staff can edit) + crew upload link */}
          <div ref={deliverablesRef} className="border border-[#E9DDC6] rounded-md p-4 bg-white space-y-2" data-testid="deliverables-section">
            <div className="flex items-center gap-2">
              <Package size={16} weight="duotone" color="#4A0000" />
              <span className="text-sm font-medium">Deliverables</span>
              {p.deliverables_ready && <span className="pill pill--paid ml-auto">Ready</span>}
            </div>
            {isStaff && (
              <input data-testid="deliverables-input" type="text" defaultValue={p.deliverables_url || ""}
                key={`del-${p.id}-${p.deliverables_url || ""}`}
                onBlur={(e) => { if ((e.target.value || "") !== (p.deliverables_url || "")) patch({ deliverables_url: e.target.value }); }}
                placeholder="Agent-facing link (Dropbox / DocSend)… (leave empty to clear)"
                className="w-full px-3 py-2 rounded-md border border-[#E9DDC6] text-sm focus:outline-none focus:ring-2 focus:ring-[#B08D57]" />
            )}
            {(() => {
              const agentLocked = user.role === "agent" && !p.client_paid;
              if (agentLocked) {
                return <button type="button" data-testid="deliverables-locked" onClick={payInvoice} disabled={paying}
                  className="flex items-center justify-center gap-2 w-full bg-[#4A0000] text-white rounded-md py-3 text-[13px] font-bold uppercase tracking-[0.08em] shadow-[0_12px_28px_-8px_rgba(74,0,0,0.65)] ring-1 ring-[#B08D57]/40 hover:bg-[#5C1A1A] transition-all disabled:opacity-70 disabled:cursor-wait">
                  <CurrencyDollar size={16} weight="fill" className="text-[#B08D57]" /> {paying ? "Starting secure checkout…" : "Pay your invoice to unlock deliverables"}
                </button>;
              }
              if (isCrew) return null;  // crew use the crew upload link below, not the agent deliverables
              return p.deliverables_url
                ? <a href={p.deliverables_url} target="_blank" rel="noreferrer" data-testid="deliverables-open"
                    className="flex items-center justify-center gap-2 w-full bg-[#4A0000] text-white rounded-md py-2 text-sm font-medium hover:bg-[#5C1A1A] transition-colors">
                    Open deliverables
                  </a>
                : <p className="text-xs text-[#5C5A58]">No deliverables link yet.</p>;
            })()}
          </div>

          {/* Crew upload link — where photographer/pilot upload raw files (staff can edit) */}
          {(isStaff || isCrew) && (
            <div className="border border-[#E9DDC6] rounded-md p-4 bg-white space-y-2" data-testid="crew-upload-section">
              <div className="flex items-center gap-2">
                <PaperPlaneTilt size={16} weight="duotone" color="#4A0000" />
                <span className="text-sm font-medium">Crew upload link</span>
                <span className="text-[11px] text-[#9A968E] ml-auto">for photographer / pilot</span>
              </div>
              {isStaff && (
                <input data-testid="crew-upload-input" type="text" defaultValue={p.crew_upload_url || ""}
                  key={`crew-${p.id}-${p.crew_upload_url || ""}`}
                  onBlur={(e) => { if ((e.target.value || "") !== (p.crew_upload_url || "")) patch({ crew_upload_url: e.target.value }); }}
                  placeholder="External upload link for crew… (leave empty to clear)"
                  className="w-full px-3 py-2 rounded-md border border-[#E9DDC6] text-sm focus:outline-none focus:ring-2 focus:ring-[#B08D57]" />
              )}
              {p.crew_upload_url
                ? <a href={p.crew_upload_url} target="_blank" rel="noreferrer" data-testid="crew-upload-open"
                    className="flex items-center justify-center gap-2 w-full bg-[#4A0000] text-white rounded-md py-2 text-sm font-medium hover:bg-[#5C1A1A] transition-colors">
                    <Package size={15} weight="fill" /> Upload your files here
                  </a>
                : <p className="text-xs text-[#5C5A58]">No upload link set yet.</p>}
            </div>
          )}

          {/* Deliverable grade — admin ↔ photographer only (agents never see it) */}
          {(isAdmin || isPhotog) && p.photographer_id && (
            <div className="border border-[#E9DDC6] rounded-md p-4 bg-white space-y-2" data-testid="grade-section">
              <div className="flex items-center gap-2">
                <ShieldCheck size={16} weight="duotone" color="#4A0000" />
                <span className="text-sm font-medium">Deliverable grade</span>
                {p.grade && <Badge className={`ml-auto border ${gradeColor(p.grade)}`} data-testid="grade-badge">{p.grade}</Badge>}
              </div>
              {isAdmin ? (
                (p.status === "completed" && p.deliverables_ready) ? (
                  <>
                    <Select value={p.grade || "none"} onValueChange={(v) => patch({ grade: v === "none" ? "" : v })}>
                      <SelectTrigger className="bg-white border-[#E9DDC6]" data-testid="grade-select"><SelectValue placeholder="Not graded" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not graded</SelectItem>
                        {GRADE_OPTIONS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <input data-testid="grade-note-input" type="text" defaultValue={p.grade_note || ""}
                      key={`grade-note-${p.id}-${p.grade_note || ""}`} placeholder="Optional feedback for the photographer…"
                      onBlur={(e) => { if ((e.target.value || "") !== (p.grade_note || "")) patch({ grade_note: e.target.value }); }}
                      className="w-full px-3 py-2 rounded-md border border-[#E9DDC6] text-sm focus:outline-none focus:ring-2 focus:ring-[#B08D57]" />
                  </>
                ) : (
                  <p className="text-xs text-[#5C5A58]">Gradeable once the shoot is completed and deliverables are ready.</p>
                )
              ) : (
                p.grade
                  ? <p className="text-xs text-[#5C5A58]">{p.grade_note || "Graded by the Reelty team."}</p>
                  : <p className="text-xs text-[#5C5A58]">Not graded yet.</p>
              )}
            </div>
          )}

          <div ref={reviewRef}>
          {/* Agent rates the photographer after a completed shoot (shown publicly) */}
          {user.role === "agent" && p.agent_id === user.id && p.status === "completed" && p.photographer_id && (
            <ReviewCard p={p} photographerName={userName(p.photographer_id)} onSaved={(np) => setP(np)} />
          )}

          {/* Admin review moderation — full control (hide / flag / delete) */}
          {isAdmin && p.review_rating && (
            <AdminReviewCard p={p} onSaved={(np) => setP(np)} />
          )}
          </div>

          {/* Admin add-on / promo — available on ANY shoot, claimed or not */}
          {isAdmin && (
            <div className="border border-[#B08D57]/50 bg-[#B08D57]/10 rounded-md p-4 space-y-2" data-testid="bonus-admin">
              <p className="text-sm font-medium text-[#4A0000]">Add-on / promo (+$)</p>
              <p className="text-xs text-[#5C5A58]">Add a bonus to this shoot's payout for any reason — applies whether or not it's claimed.</p>
              <div className="flex gap-2 pt-1">
                <input data-testid="bonus-input" type="number" defaultValue={p.bonus || 0}
                  key={`bonus-${p.id}-${p.bonus}`}
                  onBlur={(e) => { const v = Number(e.target.value) || 0; if (v !== (p.bonus || 0)) patch({ bonus: v }); }}
                  className="w-28 px-3 py-2 rounded-md border border-[#E9DDC6] text-sm font-mono-num focus:outline-none focus:ring-2 focus:ring-[#B08D57]" />
                <input data-testid="bonus-note-input" type="text" defaultValue={p.bonus_note || ""}
                  key={`note-${p.id}-${p.bonus_note || ""}`} placeholder="Reason (e.g. rush, loyalty)"
                  onBlur={(e) => { if ((e.target.value || "") !== (p.bonus_note || "")) patch({ bonus_note: e.target.value }); }}
                  className="flex-1 px-3 py-2 rounded-md border border-[#E9DDC6] text-sm focus:outline-none focus:ring-2 focus:ring-[#B08D57]" />
              </div>
              {p.bonus > 0 && (
                <p className="text-xs text-[#8a6d0a] font-mono-num pt-1" data-testid="bonus-preview">
                  +{money(p.bonus)} add-on applied{p.bonus_note ? ` · ${p.bonus_note}` : ""}
                </p>
              )}
            </div>
          )}

          {/* Delivery plan + live countdown (assigned photographer) */}
          {isPhotog && p.photographer_id === user.id && p.status !== "cancelled" && (
            <div className="space-y-3" data-testid="delivery-plan">
              <label className="flex items-center justify-between border border-[#E9DDC6] rounded-md p-4 bg-white cursor-pointer gap-3">
                <span className="min-w-0">
                  <span className="text-sm font-medium block">I'll edit these photos</span>
                  <span className="text-xs text-[#5C5A58]">
                    {p.edited
                      ? `Editing on${p.edit_fee ? ` · +${money(p.edit_fee)} pay` : ""} · 24 hours to deliver`
                      : "Raws only · deliver within 12 hours of the shoot start"}
                  </span>
                </span>
                <Switch data-testid="photog-edited-switch" checked={p.edited} disabled={p.deliverables_ready}
                  onCheckedChange={(v) => patch({ edited: v })} />
              </label>
              <DeliveryCountdown p={p} />
            </div>
          )}

          {/* Pay block */}
          {(isAdmin || isPhotog) && p.photographer_id && (
            <div className="border border-[#E9DDC6] rounded-md p-4 bg-white space-y-3" data-testid="pay-block">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#5C5A58]">Base fee{p.photographer_tier ? ` · ${p.photographer_tier} tier` : ""}</span>
                <span className="font-mono-num">{money(p.flat_fee)}</span>
              </div>
              {isAdmin && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#5C5A58]">Edited (+{money(p.edit_fee)})</span>
                  <Switch data-testid="edited-switch" checked={p.edited}
                    onCheckedChange={(v) => patch({ edited: v })} />
                </div>
              )}
              {p.bonus > 0 && (
                <div className="flex items-center justify-between border-t border-[#FAF9F6] pt-3" data-testid="bonus-row">
                  <span className="text-sm text-[#5C5A58]">Add-on{p.bonus_note ? ` · ${p.bonus_note}` : ""}</span>
                  <span className="font-mono-num text-[#8a6d0a]">+{money(p.bonus)}</span>
                </div>
              )}
              <div className="flex items-center justify-between border-t border-[#FAF9F6] pt-3">
                <span className="font-medium">Total pay</span>
                <span className="font-mono-num text-lg text-[#4A0000] font-semibold">{money((p.flat_fee || 0) + (p.bonus || 0) + (p.edited ? p.edit_fee || 0 : 0))}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#5C5A58]">Paid</span>
                {isAdmin ? (
                  <Switch data-testid="paid-switch" checked={p.paid} onCheckedChange={(v) => patch({ paid: v })} />
                ) : (
                  <span className={`pill ${p.paid ? "pill--paid" : "pill--info"}`}>{p.paid ? "Paid" : "Unpaid"}</span>
                )}
              </div>
            </div>
          )}

          {p.notes && (
            <div>
              <p className="text-xs uppercase tracking-wide text-[#5C5A58] mb-1">Notes</p>
              <p className="text-sm bg-white border border-[#E9DDC6] rounded-md p-3">{p.notes}</p>
            </div>
          )}

          <div ref={msgSectionRef}>
            <p className="text-xs uppercase tracking-wide text-[#5C5A58] mb-2">Property Messages</p>
            <Messaging propertyId={p.id} />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
